export type CMDBOperatingMode = 'report_only' | 'approval_required' | 'controlled_writeback';

export type CMDBOperatingPolicyForm = {
  updateExisting: boolean;
  createMissing: boolean;
  mode: CMDBOperatingMode;
  publicationEnabled: boolean;
  allowedFields: string[];
};

type FieldCapability = { key: string; label: string };

type Props = {
  value: CMDBOperatingPolicyForm;
  providerLabel: string;
  supportedFields: FieldCapability[];
  createMissingSupported: boolean;
  onChange: (next: CMDBOperatingPolicyForm) => void;
};

const CMDBOperatingPolicyEditor = ({ value, providerLabel, supportedFields, createMissingSupported, onChange }: Props) => {
  const setMode = (mode: CMDBOperatingMode) => {
    if (mode === 'report_only') {
      onChange({ ...value, mode, publicationEnabled: false, updateExisting: false, createMissing: false, allowedFields: [] });
      return;
    }
    onChange({ ...value, mode, publicationEnabled: mode === 'controlled_writeback' ? value.publicationEnabled : false });
  };

  const toggleField = (key: string) => {
    onChange({
      ...value,
      allowedFields: value.allowedFields.includes(key)
        ? value.allowedFields.filter((field) => field !== key)
        : [...value.allowedFields, key]
    });
  };

  return <>
    <fieldset className="integration-cmdb-mode-picker">
      <legend>What should REAP do when it finds a difference?</legend>
      {([
        ['report_only', 'Report only', 'Recommended. Show discrepancies without changing the CMDB.'],
        ['approval_required', 'Require approval', 'Prepare allowed corrections, but require review before publication.'],
        ['controlled_writeback', 'Controlled writeback', 'Publish explicitly allowed fields under server-enforced safeguards.']
      ] as const).map(([mode, label, description]) => <label key={mode} className={value.mode === mode ? 'is-selected' : ''}>
        <input type="radio" name="cmdb-operating-mode" value={mode} checked={value.mode === mode} onChange={() => setMode(mode)} />
        <span><strong>{label}</strong><small>{description}</small></span>
      </label>)}
    </fieldset>
    <div className="integration-cmdb-toggle-list">
      {value.mode === 'report_only' && <div className="ui-alert ui-alert--info">REAP will compare and report only. No update scope or publication switch is required.</div>}
      {value.mode === 'controlled_writeback' && <label className="integration-cmdb-toggle-row">
        <input type="checkbox" checked={value.publicationEnabled} onChange={(event) => onChange({ ...value, publicationEnabled: event.target.checked })} />
        <span><strong>Enable this connection</strong><small>Connection-level kill switch. Global and tenant authorization checks still apply.</small></span>
      </label>}
      {value.mode !== 'report_only' && <>
        <div className="integration-cmdb-toggle-group">
          <label className="integration-cmdb-toggle-row">
            <input type="checkbox" checked={value.updateExisting} onChange={(event) => onChange({ ...value, updateExisting: event.target.checked })} />
            <span><strong>Update matched CIs</strong><small>Push REAP changes into the existing {providerLabel} CI for bound devices.</small></span>
          </label>
          {value.updateExisting && <details className="integration-cmdb-field-disclosure">
            <summary><span>Allowed update fields</span><small>{value.allowedFields.length} of {supportedFields.length} selected</small></summary>
            <div className="integration-cmdb-field-disclosure__options">
              {supportedFields.map((field) => <label key={field.key}>
                <input type="checkbox" checked={value.allowedFields.includes(field.key)} onChange={() => toggleField(field.key)} />
                <span>{field.label}</span>
              </label>)}
            </div>
          </details>}
        </div>
        <label className="integration-cmdb-toggle-row">
          <input type="checkbox" checked={value.createMissing} disabled={!createMissingSupported} onChange={(event) => onChange({ ...value, createMissing: event.target.checked })} />
          <span><strong>Create missing CIs</strong><small>{createMissingSupported ? 'Create provider records only after server-side duplicate prevention succeeds.' : 'This provider does not currently support safe CI creation.'}</small></span>
        </label>
      </>}
    </div>
  </>;
};

export default CMDBOperatingPolicyEditor;
